import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { id: "home",     label: "HOMEPAGE",        path: "" },
  { id: "register", label: "JOIN / REGISTER", path: "/register" },
  { id: "events",   label: "EVENTS",          path: "/events" },
  { id: "challenges", label: "CHALLENGES",    path: "/challenges" },
];

const PRODUCTION_BASE = "https://odishasteelcityrunner.com";

/**
 * QRPoster — printable QR card pointing at the OSCR community site.
 * Lets the admin pick a preset destination + custom path, preview the QR
 * card, and download it as a high-resolution PNG.
 */
export default function QRPoster() {
  const [preset, setPreset] = useState("home");
  const [customPath, setCustomPath] = useState("");
  const [base, setBase] = useState(PRODUCTION_BASE);
  const posterRef = useRef(null);

  const fullUrl = useMemo(() => {
    let path = "";
    if (preset === "custom") {
      path = customPath ? (customPath.startsWith("/") ? customPath : "/" + customPath) : "";
    } else {
      path = PRESETS.find((p) => p.id === preset)?.path || "";
    }
    return `${base.replace(/\/$/, "")}${path}`;
  }, [preset, customPath, base]);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const qrSrc = `${backendBase}/api/qr?size=14&border=2&url=${encodeURIComponent(fullUrl)}`;

  const downloadPng = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `oscr-qr-${preset}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const printPoster = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="qr-poster">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .qr-poster-printable, .qr-poster-printable * { visibility: visible; }
          .qr-poster-printable { position: absolute; inset: 0; background: white !important; }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Controls */}
        <div className="lg:col-span-2 hairline bg-zinc-900 p-5 space-y-4 h-fit">
          <div>
            <h3 className="font-display text-xl text-white tracking-wider">QR CODE GENERATOR</h3>
            <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mt-1">
              Print on flyers · banners · t-shirts
            </p>
          </div>

          <div>
            <Label className="font-display tracking-widest text-xs text-zinc-400">DESTINATION</Label>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`qr-preset-${p.id}`}
                  className={`px-3 py-2 hairline font-display tracking-wider text-[10px] transition ${
                    preset === p.id
                      ? "bg-orange-600 text-white border-orange-500"
                      : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-orange-600/40"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => setPreset("custom")}
                data-testid="qr-preset-custom"
                className={`col-span-2 px-3 py-2 hairline font-display tracking-wider text-[10px] transition ${
                  preset === "custom"
                    ? "bg-orange-600 text-white border-orange-500"
                    : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-orange-600/40"
                }`}
              >
                CUSTOM PATH
              </button>
            </div>
          </div>

          {preset === "custom" && (
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">CUSTOM PATH</Label>
              <Input
                data-testid="qr-custom-path"
                value={customPath}
                onChange={(e) => setCustomPath(e.target.value)}
                placeholder="/profile/user_..."
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600"
              />
            </div>
          )}

          <div>
            <Label className="font-display tracking-widest text-xs text-zinc-400">BASE URL</Label>
            <Input
              data-testid="qr-base-url"
              value={base}
              onChange={(e) => setBase(e.target.value)}
              className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 font-mono text-xs"
            />
            <p className="font-mono text-[9px] text-zinc-500 mt-1 tracking-widest uppercase">
              Defaults to production. Edit only if testing on preview.
            </p>
          </div>

          <div className="hairline bg-zinc-950 border-zinc-800 p-3">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">RESOLVED URL</p>
            <p className="font-mono text-xs text-orange-500 break-all mt-1">{fullUrl}</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={downloadPng}
              data-testid="qr-download-btn"
              className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider text-xs"
            >
              <Download size={14} className="mr-1" /> PNG
            </Button>
            <Button
              onClick={printPoster}
              data-testid="qr-print-btn"
              variant="outline"
              className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs"
            >
              PRINT
            </Button>
            <Button
              onClick={copyUrl}
              data-testid="qr-copy-btn"
              variant="outline"
              className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs"
            >
              <Share2 size={14} className="mr-1" /> COPY URL
            </Button>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="qr-open-btn"
              className="inline-flex items-center justify-center rounded-none border border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider text-xs px-3 py-2"
            >
              <ExternalLink size={14} className="mr-1" /> OPEN
            </a>
          </div>
        </div>

        {/* Poster preview */}
        <div className="lg:col-span-3">
          <div
            ref={posterRef}
            className="qr-poster-printable bg-white p-8 sm:p-10 mx-auto"
            style={{ maxWidth: 560 }}
            data-testid="qr-poster-card"
          >
            <div className="flex items-center justify-between border-b-4 border-orange-600 pb-3">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-orange-600 uppercase">
                  EST · ROURKELA · 2026
                </p>
                <p className="font-display text-2xl text-black tracking-wider mt-1">
                  OSCR<span className="text-orange-600">.</span>
                </p>
              </div>
              <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-700 uppercase text-right">
                ODISHA STEEL<br />CITY RUNNERS
              </p>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl text-black leading-tight mt-6 tracking-tight">
              Scan to <span className="text-orange-600">join the pack.</span>
            </h2>
            <p className="text-zinc-700 mt-2 text-sm">
              Run further, together. One state. One pack.
            </p>

            <div className="mt-6 flex items-center justify-center bg-zinc-50 p-3 border border-zinc-200">
              <img
                src={qrSrc}
                alt={`QR code for ${fullUrl}`}
                data-testid="qr-image"
                className="w-full max-w-[320px] h-auto"
              />
            </div>

            <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase mt-4 text-center break-all">
              {fullUrl.replace(/^https?:\/\//, "")}
            </p>

            <div className="mt-6 pt-4 border-t border-zinc-200 flex items-center justify-between">
              <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">
                FOUNDER · SUMIT SINGH<br />2× GUINNESS WORLD RECORDS
              </p>
              <p className="font-mono text-[9px] tracking-[0.25em] text-orange-600 uppercase text-right">
                FROM EVERY DISTRICT<br />WE RUN AS ONE
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
